import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ROLE_LABELS, TEAM_ROLE_LABELS, type EntryBrand, type Role, type TeamRole } from '@kaba/shared';
import { api } from '../lib/api';
import { queryClient } from '../lib/board';
import { signIn, signOut, useMe } from '../lib/auth';
import { navigate } from '../lib/router';
import { Button } from './ui/ui';
import { PasswordInput } from './ui/PasswordInput';
import { input, Shell, translate } from './LoginPage';

interface InviteInfo {
  email: string;
  role: Role | TeamRole;
  boardTitle?: string;
  teamName?: string;
  inviterName: string | null;
  expiresAt: string | null;
  accountExists: boolean;
  /** Teameinladung: Gruppen, die mit dem Beitritt wirksam werden */
  groups?: string[];
  /** Board-Einladung: Logo und Farbe des Boards */
  brand?: EntryBrand | null;
}

/** Ziel des Einladungslinks: /einladung?token=… (Board) bzw. /team-einladung?token=… (Team) */
export function InvitePage({ kind = 'board' }: { kind?: 'board' | 'team' }) {
  const token = new URLSearchParams(location.search).get('token') ?? '';
  const base = kind === 'team' ? '/api/team-invitations' : '/api/invitations';
  const { data: me } = useMe();
  const info = useQuery({
    queryKey: ['invite', kind, token],
    queryFn: () => api.get<InviteInfo>(`${base}/${encodeURIComponent(token)}`),
    retry: false,
    enabled: !!token,
  });
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async (fn: () => Promise<void>) => {
    setError(null);
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      setError(translate((err as Error).message));
    } finally {
      setBusy(false);
    }
  };

  const accept = async () => {
    const res = await api.post<{ boardId?: string; teamId?: string }>(`${base}/${encodeURIComponent(token)}/accept`, {});
    await queryClient.invalidateQueries();
    navigate(res.teamId ? `/teams/${res.teamId}` : `/b/${res.boardId}`);
  };

  if (!token || info.isError)
    return (
      <Shell title="Einladung" brand={info.data?.brand}>
        <p className="text-sm">
          {info.error?.message ?? 'Der Einladungslink ist ungültig, abgelaufen oder wurde schon benutzt. Bitte lass dich erneut einladen.'}
        </p>
        <Button variant="primary" onClick={() => navigate('/')}>
          Zur Startseite
        </Button>
      </Shell>
    );
  if (!info.data) return <Shell title="Einladung">Lade …</Shell>;
  const inv = info.data;

  const intro =
    kind === 'team' ? (
      <>
        <p className="text-sm">
          {inv.inviterName ? `${inv.inviterName} lädt dich` : 'Du bist'} ins Team <strong>„{inv.teamName}“</strong> ein – als{' '}
          {TEAM_ROLE_LABELS[inv.role as TeamRole]}.
        </p>
        {inv.groups && inv.groups.length > 0 && (
          <p className="text-sm">
            Mit dem Beitritt kommst du in die {inv.groups.length === 1 ? 'Gruppe' : 'Gruppen'} <strong>{inv.groups.join(', ')}</strong> und erhältst Zugriff auf
            deren freigegebene Boards.
          </p>
        )}
        <p className="text-sm text-[var(--ink-soft)]">Die Teammitgliedschaft allein gibt keinen Boardzugriff. Du nimmst nur diese eine Einladung an.</p>
      </>
    ) : (
      <p className="text-sm">
        {inv.inviterName ? `${inv.inviterName} lädt dich` : 'Du bist'} zum Board <strong>„{inv.boardTitle}“</strong> ein – als{' '}
        {ROLE_LABELS[inv.role as Role]}.
      </p>
    );
  const errorBox = error && (
    <p role="alert" className="rounded-lg bg-rose-50 p-2.5 text-sm text-rose-700 dark:bg-rose-950 dark:text-rose-200">
      {error}
    </p>
  );

  // Angemeldet
  if (me) {
    const matches = me.email.toLowerCase() === inv.email;
    return (
      <Shell title="Einladung" brand={info.data?.brand}>
        {intro}
        {matches ? (
          <Button variant="primary" className="min-h-12" disabled={busy} onClick={() => run(accept)}>
            Einladung annehmen
          </Button>
        ) : (
          <>
            <p className="text-sm text-[var(--ink-soft)]">
              Die Einladung gilt für <strong>{inv.email}</strong>, du bist aber als {me.email} angemeldet.
            </p>
            <Button onClick={() => void signOut()}>Abmelden</Button>
          </>
        )}
        {errorBox}
      </Shell>
    );
  }

  // Nicht angemeldet: Konto vorhanden → anmelden; sonst Konto anlegen
  return (
    <Shell title="Einladung" brand={info.data?.brand}>
      {intro}
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          void run(async () => {
            if (inv.accountExists) {
              await signIn(inv.email, password);
              await accept();
            } else {
              await api.post<unknown>(`${base}/${encodeURIComponent(token)}/register`, { name, password });
              // Team: genau diese Einladung ausdrücklich annehmen (F03); Board-Einladungen nimmt die Registrierung an
              if (kind === 'team') await accept();
              else {
                await queryClient.invalidateQueries();
                navigate('/');
              }
            }
          });
        }}
      >
        <input className={input} type="email" value={inv.email} readOnly aria-label="E-Mail" />
        {!inv.accountExists && (
          <input className={input} placeholder="Name" autoComplete="name" required value={name} onChange={(e) => setName(e.target.value)} />
        )}
        <PasswordInput
          className={input}
          placeholder={inv.accountExists ? 'Passwort' : 'Passwort wählen'}
          autoComplete={inv.accountExists ? 'current-password' : 'new-password'}
          minLength={8}
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        {errorBox}
        <Button type="submit" variant="primary" disabled={busy} className="min-h-12">
          {inv.accountExists ? 'Anmelden und annehmen' : 'Konto anlegen und beitreten'}
        </Button>
      </form>
    </Shell>
  );
}
